import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import CtaBand from "@/components/CtaBand";
import { Reveal } from "@/components/Reveal";
import { CASE_STUDIES, IN_PREPARATION, DISCLOSURE } from "@/data/experience";

export default function ExperiencePage() {
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash) return;
    const el = document.getElementById(hash.slice(1));
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 90;
      if (window.__lenis) window.__lenis.scrollTo(y, { immediate: true });
      else window.scrollTo(0, y);
    }
  }, [hash]);

  return (
    <>
      <Seo
        title="Representative Experience — Improve Systems"
        siteName="Improve Systems"
        description="Anonymized case studies from the prior professional experience of Improve Systems' principal — root-cause analysis, cross-system tracing, and AI-assisted operational intelligence, stated with real volumes, methods, and outcomes."
      />
      <PageHero
        eyebrow="REPRESENTATIVE EXPERIENCE"
        title="The work, stated specifically."
        lede="These case studies describe prior professional experience of Improve Systems' principal. Organization and product identifiers have been omitted to protect confidentiality — but the volumes, systems, methodology, decisions, and outcomes are stated as they happened."
        testid="experience-hero"
      />

      <section data-testid="case-studies" className="border-b border-line">
        <div className="mx-auto max-w-7xl px-6 py-20 sm:py-28">
          <Reveal>
            <p className="mb-6 font-mono text-[11px] tracking-[0.3em] text-slate-500">
              01 — THE PATTERN ACROSS ALL OF THEM
            </p>
            <p className="max-w-3xl font-serif text-2xl leading-snug text-ink sm:text-3xl">
              Problem → Discovery → Evidence → Root Cause → Requirements → Intervention →
              Outcome
            </p>
            <p className="mt-5 max-w-2xl leading-relaxed text-slate-600">
              None of these are software-company stories. They are product transformation
              stories — each one demonstrating a different part of the advisory lifecycle
              Improve Systems now offers as a practice.
            </p>
          </Reveal>

          <div className="mt-20 space-y-24">
            {CASE_STUDIES.map((cs) => (
              <article
                key={cs.slug}
                id={cs.slug}
                data-testid={`case-study-${cs.slug}`}
                className="scroll-mt-24 border-t-2 border-ink pt-10"
              >
                <Reveal>
                  <div className="grid gap-6 lg:grid-cols-12">
                    <div className="lg:col-span-1">
                      <span className="font-serif text-5xl leading-none text-slate-200">
                        {cs.index}
                      </span>
                    </div>
                    <div className="lg:col-span-11">
                      <p className="font-mono text-[10px] tracking-[0.25em] text-slate-500">
                        {cs.tag}
                      </p>
                      <h2 className="mt-3 max-w-3xl font-serif text-3xl leading-tight tracking-tight text-ink sm:text-4xl">
                        {cs.title}
                      </h2>
                      <p className="mt-4 max-w-2xl font-mono text-xs leading-relaxed tracking-wide text-slate-500">
                        {cs.context}
                      </p>
                      <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate-700 sm:text-lg">
                        {cs.summary}
                      </p>
                    </div>
                  </div>
                </Reveal>

                <div className="mt-12 border-t border-line">
                  {cs.lifecycle.map((step, i) => (
                    <Reveal key={step.label} delay={i * 0.03}>
                      <div className="grid gap-3 border-b border-line py-6 lg:grid-cols-12">
                        <p className="font-mono text-[10px] tracking-[0.25em] text-slate-500 lg:col-span-3">
                          {String(i + 1).padStart(2, "0")} — {step.label.toUpperCase()}
                        </p>
                        <p className="max-w-3xl leading-relaxed text-slate-700 lg:col-span-9">
                          {step.text}
                        </p>
                      </div>
                    </Reveal>
                  ))}
                </div>

                <Reveal>
                  <div className="mt-8 border-l-2 border-accent bg-white p-6">
                    <p className="font-mono text-[10px] tracking-[0.25em] text-slate-500">
                      WHAT THIS DEMONSTRATES
                    </p>
                    <p className="mt-2 max-w-3xl font-serif text-lg italic leading-snug text-ink">
                      {cs.demonstrates}
                    </p>
                  </div>
                  <p className="mt-6 font-mono text-[10px] leading-relaxed tracking-[0.15em] text-slate-400">
                    {DISCLOSURE.toUpperCase()}
                  </p>
                </Reveal>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="experience-in-preparation" className="border-b border-line bg-white">
        <div className="mx-auto max-w-7xl px-6 py-20 sm:py-24">
          <Reveal>
            <p className="mb-6 font-mono text-[11px] tracking-[0.3em] text-slate-500">
              02 — IN PREPARATION
            </p>
            <h2 className="max-w-xl font-serif text-3xl leading-tight tracking-tight text-ink sm:text-4xl">
              Two further case studies in development.
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-px border border-line bg-line lg:grid-cols-2">
            {IN_PREPARATION.map((item, i) => (
              <Reveal key={item.title} delay={i * 0.08} className="h-full">
                <div className="flex h-full flex-col bg-white p-8">
                  <span className="font-mono text-[11px] text-slate-400">
                    {String(i + 4).padStart(2, "0")}
                  </span>
                  <h3 className="mt-4 font-serif text-2xl leading-snug text-ink">
                    {item.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">{item.note}</p>
                  <p className="mt-auto pt-6 font-mono text-[10px] tracking-[0.25em] text-slate-400">
                    CASE STUDY IN PREPARATION
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CtaBand
        title="Want this pattern applied to your system?"
        copy="Every engagement starts the same way these case studies did: understanding what is actually happening before deciding what to change."
      />
    </>
  );
}
